import { supabase } from '../lib/supabase';

/**
 * Automation Queue Service - READ ONLY
 * Aggregates queue metrics from existing wp_articles table for dashboard
 * No writing to queue - just passive monitoring
 *
 * Every function accepts an optional profileId — when provided, scopes the
 * query to that language profile's articles only, so the queue/dashboard
 * never mixes data between profiles (Etsy has no profile_id, unaffected).
 */
export const automationQueueService = {
  /**
   * Get today's job counts by status from wp_articles
   */
  async getTodayJobStats(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      let query = supabase
        .from('wp_articles')
        .select('status')
        .eq('user_id', user.id)
        .gte('created_at', todayISO);
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query;

      if (error) throw error;

      // Calculate stats from existing data
      const stats = {
        total: data?.length || 0,
        pending: data?.filter(j => j.status === 'queued').length || 0,
        processing: data?.filter(j => j.status === 'processing').length || 0,
        completed: data?.filter(j => j.status === 'completed').length || 0,
        failed: data?.filter(j => j.status === 'error').length || 0,
        generating: data?.filter(j => j.status === 'generating').length || 0,
        draft: data?.filter(j => j.status === 'draft').length || 0,
      };

      return { stats, error: null };
    } catch (error) {
      console.error('Error getting today job stats:', error);
      return { stats: { total: 0, pending: 0, processing: 0, completed: 0, failed: 0 }, error };
    }
  },

  /**
   * Get pending jobs (queued) - for display only
   */
  async getPendingJobs(limit = 10, profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('wp_articles')
        .select('id, title, focus_keyword, status, created_at, error_message')
        .eq('user_id', user.id)
        .eq('status', 'queued');
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error getting pending jobs:', error);
      return { data: [], error };
    }
  },

  /**
   * Get oldest queued job age in minutes
   */
  async getOldestPendingJobAge(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('wp_articles')
        .select('created_at')
        .eq('user_id', user.id)
        .eq('status', 'queued');
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (error || !data) return { ageMinutes: 0, error: null };

      const createdAt = new Date(data.created_at);
      const now = new Date();
      const ageMinutes = Math.floor((now - createdAt) / 60000);

      return { ageMinutes, error: null };
    } catch (error) {
      console.error('Error getting oldest job age:', error);
      return { ageMinutes: 0, error };
    }
  },

  /**
   * Get average processing time in seconds (from completed jobs)
   */
  async getAverageProcessingTime(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get completed jobs from last 7 days for stats
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      let query = supabase
        .from('wp_articles')
        .select('created_at, updated_at')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .gte('created_at', weekAgo.toISOString());
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query;

      if (error) throw error;

      if (!data?.length) return { avgSeconds: 0, jobCount: 0 };

      // Calculate average processing time
      let totalSeconds = 0;
      let validJobs = 0;

      data.forEach(job => {
        if (job.created_at && job.updated_at) {
          const start = new Date(job.created_at);
          const end = new Date(job.updated_at);
          const seconds = (end - start) / 1000;
          if (seconds > 0 && seconds < 3600) { // Sanity check: under 1 hour
            totalSeconds += seconds;
            validJobs++;
          }
        }
      });

      const avgSeconds = validJobs > 0 ? Math.round(totalSeconds / validJobs) : 0;

      return { avgSeconds, jobCount: validJobs, error: null };
    } catch (error) {
      console.error('Error getting avg processing time:', error);
      return { avgSeconds: 0, jobCount: 0, error };
    }
  },

  /**
   * Get jobs by date range for chart data
   */
  async getJobsByDateRange(days = 30, profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      let query = supabase
        .from('wp_articles')
        .select('status, created_at')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString());
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date and status
      const dateMap = {};

      data?.forEach(job => {
        const date = new Date(job.created_at).toISOString().split('T')[0];

        if (!dateMap[date]) {
          dateMap[date] = {
            date,
            wordpress: 0,
            pinterest: 0,
            facebook: 0,
            total: 0
          };
        }

        // All wp_articles are WordPress jobs
        dateMap[date].wordpress++;
        dateMap[date].total++;
      });

      return { data: Object.values(dateMap), error: null };
    } catch (error) {
      console.error('Error getting jobs by date:', error);
      return { data: [], error };
    }
  },

  /**
   * Get failed jobs today - for alerts
   */
  async getFailedJobsToday(limit = 10, profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let query = supabase
        .from('wp_articles')
        .select('id, title, focus_keyword, status, created_at, error_message')
        .eq('user_id', user.id)
        .eq('status', 'error')
        .gte('created_at', today.toISOString());
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error getting failed jobs:', error);
      return { data: [], error };
    }
  },

  /**
   * Get processing jobs - for queue status
   */
  async getProcessingJobs(profileId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('wp_articles')
        .select('id, title, created_at')
        .eq('user_id', user.id)
        .eq('status', 'processing');
      if (profileId) query = query.eq('profile_id', profileId);
      const { data, error } = await query.limit(5);

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error getting processing jobs:', error);
      return { data: [], error };
    }
  }
};

// Export individual functions
export const getTodayJobStats = automationQueueService.getTodayJobStats;
export const getPendingJobs = automationQueueService.getPendingJobs;
export const getOldestPendingJobAge = automationQueueService.getOldestPendingJobAge;
export const getAverageProcessingTime = automationQueueService.getAverageProcessingTime;
export const getJobsByDateRange = automationQueueService.getJobsByDateRange;
export const getFailedJobsToday = automationQueueService.getFailedJobsToday;
export const getProcessingJobs = automationQueueService.getProcessingJobs;
